import React, {
    useCallback, useMemo, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Button, notification, Table } from 'ui-library';
import { useParams } from 'react-router-dom';
import Infobox from 'ui-library/components/Infobox/Infobox';
import { useGetContact } from 'hooks/rest/useGetContact';
import { useLayout } from 'prodivers/layout';
import Icon from 'ui-library/components/Icon/Icon';
import { ICON_TYPE_LETTER } from 'ui-library/components/Icon';
import { getClientId } from 'utils';
import { getSetupData } from 'additiv-services/setup';
import { ProxyOrderNotProcessed } from 'constants/constants';
import { useGetCustomService } from 'hooks/rest/useGetCustomService';
import { useClientActivationHistory } from './hooks/useClientActivationHistory';
import Card from '../../../../components/Card/Card';
import Preloader from '../../../../components/Preloader';
import { clientHistoryAdapter } from './adapter/clientHistoryAdapter';
import { useResendActivationLatter } from './hooks/useSendActivationLatter';
import { columns } from './const';
import { useClientProfile } from '../../../../prodivers/clientProfile';
import { useClientLayout } from '../../hooks/useClientLayout';
import './ClientPortal.css';

const setupData = getSetupData();

const ClientPortal = () => {
    const { t } = useTranslation();
    const { setLoadingOverlayActive } = useLayout() as any;
    const [resendError, setResendError] = useState<string | undefined>();
    const { dfsClientId } = useParams();
    const clientId = getClientId(dfsClientId);
    const { data: contactDetails } = useGetContact({ contactId: clientId, enabled: !!clientId });
    const { isOverviewLoading } = useClientProfile();
    const {
        getData, data, isLoading: isHistoryLoading, error, isLastResend24hAgo, isUserRegistered,
    } = useClientActivationHistory({ contactId: clientId, adapter: clientHistoryAdapter });

    useClientLayout();

    const contactPhoneNumber = !!contactDetails?.details?.personalInformation?.primaryPhoneNumber;

    const { data: isReSendEnabledForClientFromAPI, isLoading: isReSendEnabledForClientLoading, refetch: refetchIsReSendEnabledForClient } = useGetCustomService({
        service: 'customClientService',
        api: 'resendActivationLetterEnabled',
        payload: [{ contactId: clientId }],
        enabled: !!clientId,
        gcTime: 3 * 1000, // 3 seconds
        staleTime: 2 * 1000, // 2 seconds
    });

    const isReSendEnabledForClient = isReSendEnabledForClientFromAPI?.data;

    const { sendData, isLoading: isSending } = useResendActivationLatter({ contactId: clientId });

    const resendActivationCode = useCallback(async () => {
        setLoadingOverlayActive(true);
        try {
            setResendError(undefined);
            await sendData();

            await getData();
            await refetchIsReSendEnabledForClient();
        } catch (err) {
            if ((err as any)?.status === 400 && (err as any)?.response?.data) {
                let message = (err as any).response.data;

                if (message === ProxyOrderNotProcessed) {
                    message = t('connections.addConnection.Message.CanNotAddProxy');
                }

                setResendError(message);
                notification.open({ content: message, type: 'error' });
            } else {
                const message = t('clientDashboard.clientPortal.letterNotDelivered');

                setResendError(message);
                notification.open({ content: message, type: 'error' });
            }
        } finally {
            setLoadingOverlayActive(false);
        }
    }, [sendData, getData, refetchIsReSendEnabledForClient]);

    const openUserInBO = useCallback(() => {
        const zurichInvestBO = setupData.BO_ZIAG_URL;
        const additivBO = setupData.BO_ADDITIV_URL;
        const isZurichInvest = window.location.href.includes('zurich.ch');

        global.open(`${isZurichInvest ? zurichInvestBO : additivBO}/Additiv/Crm/Contact/Edit/${clientId}`, '_blank');
    }, [clientId]);

    const isLoading = useMemo(() => isHistoryLoading || isSending || isOverviewLoading || isReSendEnabledForClientLoading, [isHistoryLoading, isSending, isOverviewLoading, isReSendEnabledForClientLoading]);

    const isResendDisabled = useMemo(() => (
        isLoading || !isReSendEnabledForClient || !isLastResend24hAgo || isUserRegistered || !contactPhoneNumber
    ), [isLoading, isReSendEnabledForClient, isLastResend24hAgo, isUserRegistered, contactPhoneNumber]);

    return (
        <div className="container ClientPortal fi-verticals">
            <Card
                title={t('clientDashboard.clientPortal.title')}
                titleTagName="h1"
            >
                <Preloader isLoading={isHistoryLoading} error={error}>
                    <Table
                        noDataText={t('clientDashboard.clientPortal.noActions')}
                        columns={columns(t)}
                        data={data}
                    />
                </Preloader>
                {resendError && <Infobox error>{resendError}</Infobox>}
                <span className="button-group">
                    <Button
                        type="secondary"
                        size="small"
                        className="letter"
                        loading={isLoading}
                        disabled={isResendDisabled}
                        onClick={resendActivationCode}
                    >
                        <Icon type={ICON_TYPE_LETTER} size={24} />
                        {t('clientDashboard.clientPortal.activation.buttonLabel')}
                    </Button>

                    <Button
                        type="secondary"
                        size="small"
                        className="open-bo"
                        onClick={openUserInBO}
                    >
                        {t('clientDashboard.clientPortal.openBO.buttonLabel')}
                    </Button>
                </span>
            </Card>
        </div>
    );
};

export default ClientPortal;
